// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { Option } from "@insite/client-framework/Types/FieldDefinition";
import { TabDefinition } from "@insite/client-framework/Types/TabDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ThemeTypography } from "@insite/mobius/globals/baseTheme";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Card from "@store/Library/Widgets/Card/Card";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import CardHeader from "@store/Library/Widgets/Card/CardHeader";
import Typography from "@store/MobiusOverrides/Typography/Typography";
import parse from "html-react-parser";

import {
    alignmentOptions,
    Alignments,
    alignmentStylesDictionary,
    borderRadiusField,
    elevationOptions,
    Elevations,
    getAlignments,
    heightField,
    widthField,
} from "@store/Library/Widgets/LinkCard/LinkCard";
import React, { useContext } from "react";
import { css, useTheme } from "styled-components";

const enum fields {
    titleStyle = "titleStyle",
    textStyle = "textStyle",
    titleColor = "titleColor",
    textColor = "textColor",
    borderRadius = "borderRadius",
    contentAlignment = "contentAlignment",
    width = "width",
    height = "height",
    elevation = "elevation",
    headerFormat = "headerFormat",
    textFormat = "textFormat",
    overrideHtmlContent = "overrideHtmlContent",
}
interface CategoryHeaderCardStyles {
    wrapper?: InjectableCss;
}

export const styles: CategoryHeaderCardStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
        `,
    },
};

type FieldProps = {
    [fields.borderRadius]: string;
    [fields.width]: string;
    [fields.height]: string;
    [fields.contentAlignment]: Alignments;
    [fields.elevation]: Elevations;
    [fields.titleStyle]: keyof ThemeTypography | undefined;
    [fields.textStyle]: keyof ThemeTypography | undefined;
    [fields.titleColor]: string;
    [fields.textColor]: string;
    [fields.overrideHtmlContent]: boolean;
};

interface CategoryHeaderCard extends WidgetProps {
    fields: FieldProps;
}

const overrideHtmlContentStyles = css`
    > * {
        color: inherit !important;
        background-color: inherit !important;
        font-size: inherit !important;
        font-weight: inherit !important;

        * {
            color: inherit !important;
            background-color: inherit !important;
            font-size: inherit !important;
            font-weight: inherit !important;
        }
    }
`;
const CategoryHeaderCard: React.FC<CategoryHeaderCard> = ({
    fields: {
        borderRadius,
        width,
        height,
        contentAlignment,
        elevation,
        titleStyle,
        textStyle,
        titleColor,
        textColor,
        overrideHtmlContent,
    },
}) => {
    const theme = useTheme();
    const category = useContext(CategoryContext);
    if (!category) {
        return null;
    }
    const shadowDictionary: Partial<Record<Elevations, string>> = {
        small: theme.shadows[1],
        medium: theme.shadows[2],
        large: theme.shadows[3],
        none: "none",
    };
    const { shortDescription, htmlContent, largeImagePath } = category;
    const cardStyles = {
        css: css`
            width: ${width};
            height: ${height};
            border-radius: ${borderRadius};
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
            background-image: url(${largeImagePath});
            box-shadow: ${shadowDictionary[elevation]};
            ${getAlignments(contentAlignment)};
        `,
    };

    const headerProps = {
        variant: titleStyle,
        css: css`
            color: ${titleColor};
        `,
    };
    const textProps = {
        variant: textStyle,
        css: css`
            color: ${textColor};
            ${overrideHtmlContent ? overrideHtmlContentStyles : ""}
        `,
    };

    const headerStyles = {
        css: css`
            display: flex;
            ${alignmentStylesDictionary[contentAlignment]};
        `,
    };
    const bodyComponentStyles = {
        css: css`
            display: flex;

            ${alignmentStylesDictionary[contentAlignment]};
        `,
    };

    const parsedHtmlContent = parse(htmlContent ?? "", parserOptions);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Card size="xsmall" direction="column" extendedStyles={cardStyles}>
                <CardHeader extendedStyles={headerStyles}>
                    <Typography {...headerProps}>{shortDescription}</Typography>
                </CardHeader>
                <CardBodyComponent extendedStyles={bodyComponentStyles}>
                    <Typography {...textProps}>{parsedHtmlContent}</Typography>
                </CardBodyComponent>
            </Card>
        </StyledWrapper>
    );
};

const layoutTab: TabDefinition = {
    displayName: "Layout",
    sortOrder: 0,
};

const contentTab: TabDefinition = {
    displayName: "Content",
    sortOrder: 1,
};
const textStyleOptions: Option<keyof ThemeTypography | "">[] = [
    { displayName: "None", value: "" },
    { displayName: "Paragraph", value: "p" },
    { displayName: "Heading 1", value: "h1" },
    { displayName: "Heading 2", value: "h2" },
    { displayName: "Heading 3", value: "h3" },
    { displayName: "Heading 4", value: "h4" },
    { displayName: "Heading 5", value: "h5" },
    { displayName: "Heading 6", value: "h6" },
];
const widgetModule: WidgetModule<Bunzl.BunzlDefinition> = {
    component: CategoryHeaderCard,
    definition: {
        group: "Categories",
        icon: "LinkCard",
        displayName: "Category Header Card",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.contentAlignment,
                displayName: "Alignment",
                editorTemplate: "DropDownField",
                defaultValue: "topCenter",
                fieldType: "General",
                options: alignmentOptions,
                tab: layoutTab,
            },

            {
                displayName: "Card Width",
                ...widthField.definition,
                defaultValue: "80%",
                tab: layoutTab,
            },

            {
                displayName: "Card Height",
                ...heightField.definition,
                defaultValue: "40vh",
                tab: layoutTab,
            },

            {
                displayName: "Card Border Radius",
                ...borderRadiusField.definition,
                defaultValue: "0.75rem",
                tab: layoutTab,
            },
            {
                name: fields.elevation,
                displayName: "Elevation",
                editorTemplate: "DropDownField",
                options: elevationOptions,
                defaultValue: "large",
                fieldType: "General",
                tab: layoutTab,
            },
            {
                name: fields.titleStyle,
                displayName: "Title Style",
                tab: contentTab,
                editorTemplate: "DropDownField",
                options: textStyleOptions,
                defaultValue: "h1",
                fieldType: "General",
            },
            {
                name: fields.textStyle,
                displayName: "Text Style",
                tab: contentTab,
                options: textStyleOptions,
                editorTemplate: "DropDownField",
                defaultValue: "p",
                fieldType: "General",
            },
            {
                name: fields.titleColor,
                displayName: "Title Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
            },
            {
                name: fields.textColor,
                displayName: "Text Color",
                editorTemplate: "CustomColorPickerField",
                defaultValue: "",
                fieldType: "General",
                tab: contentTab,
            },
            {
                name: fields.overrideHtmlContent,
                displayName: "Override Content Styles",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: contentTab,
                isVisible: (_, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
            },
        ],
    },
};

export default widgetModule;
